import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Info } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasTenantWidePermission } from '@/stores/auth';
import { Button } from '@/components/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * `/me/income` (v0.4 章⑤ F-4、D-10/D-12/D-17/D-51/D-63/D-89)。
 * 本人向けの年収の壁 (残枠) 閲覧画面。「当社分のみの金額」であることを必ず明示し、
 * 掛け持ちフラグ保有者にのみ追加警告を出す (D-51、全員に出すとアラート疲れで無視される)。
 * ⚠️ 他社収入の自己申告欄は作らない (§5-43-2 の意図的な不作為、D-17/D-63)。
 * 設計: docs/design/v04-shift-labor-management-detail.md §5-5 / §5-10
 */

const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 1, currentYear, currentYear + 1];

type IncomeCapSummary = {
  has_data: boolean;
  cap_year?: number;
  confirmed_to_date?: number;
  confirmed_through_month?: string | null;
  projected_year_end?: number;
  self_declared_cap_amount?: number | null;
  message_own_company_only: string;
  message_multiple_employers?: string | null;
};

const yen = (n: number) => `${n.toLocaleString('ja-JP')}円`;

function MySummaryCard() {
  const [year, setYear] = useState(currentYear);
  const { data, isLoading, isError, error } = useQuery<IncomeCapSummary>({
    queryKey: ['income-cap-me', year],
    queryFn: () => apiFetch<IncomeCapSummary>(`/income-cap/me?year=${year}`),
  });

  return (
    <div className="border border-surface-border rounded-lg bg-white p-4 sm:p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-h2">自分の年収残枠</h2>
        <div className="w-32">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="min-h-[44px]" aria-label="対象年"><SelectValue /></SelectTrigger>
            <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}年</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : isError ? (
        <p className="text-sm text-alert-text">{error instanceof ApiClientError ? error.message : '年収残枠の取得に失敗しました'}</p>
      ) : !data?.has_data ? (
        <p className="text-sm text-text-sub">{data?.message_own_company_only ?? '当社分のみの金額です'}</p>
      ) : (
        <div className="space-y-3">
          <div className="rounded border border-surface-line bg-surface-sunken px-3 py-2 flex items-start gap-2">
            <Info size={16} className="text-text-sub shrink-0 mt-0.5" aria-hidden />
            <p className="text-sm text-text-body">{data.message_own_company_only}</p>
          </div>
          {data.message_multiple_employers && (
            <div className="rounded border border-alert-border bg-alert-bg px-3 py-2 flex items-start gap-2">
              <AlertTriangle size={16} className="text-alert-text shrink-0 mt-0.5" aria-hidden />
              <p className="text-sm text-alert-text">{data.message_multiple_employers}</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded border border-surface-line p-3">
              <p className="text-xs text-text-sub">確定累計 ({data.confirmed_through_month ?? '未確定'}まで)</p>
              <p className="text-xl font-bold text-text-body tabular-nums mt-1">{yen(data.confirmed_to_date ?? 0)}</p>
              <p className="text-xs text-text-sub mt-1">壁の判定はこの確定累計を正として行われます。</p>
            </div>
            <div className="rounded border border-surface-line p-3">
              <p className="text-xs text-text-sub">このペースでの年内見込み</p>
              <p className="text-xl font-bold text-text-body tabular-nums mt-1">{yen(data.projected_year_end ?? 0)}</p>
              <p className="text-xs text-text-sub mt-1">シフト予定・勤怠実績を含む速報値です。目安としてご確認ください。</p>
            </div>
          </div>
          {data.self_declared_cap_amount != null && (
            <p className="text-sm text-text-sub">
              希望する年収上限: <span className="font-semibold text-text-body tabular-nums">{yen(data.self_declared_cap_amount)}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

type Employee = { employee_id: string; employee_number: string; family_name: string; given_name: string; status: string };

function AccumulationEntryPanel() {
  const qc = useQueryClient();
  const { data: employees = [] } = useQuery<Employee[]>({ queryKey: ['employees'], queryFn: () => apiFetch<Employee[]>('/employees') });
  const [employeeId, setEmployeeId] = useState('');
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [amount, setAmount] = useState('');

  const saveMut = useMutation({
    mutationFn: () => apiFetch('/income-cap/accumulations', {
      method: 'POST',
      body: JSON.stringify({
        employee_id: employeeId, cap_year: year, period_month: `${year}-${month}`,
        source_kind: 'manual_confirmed', amount: Number(amount) || 0,
      }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['income-cap-me'] }); toast.success('記録しました'); setAmount(''); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '記録に失敗しました'),
  });

  const canSave = !!employeeId && Number(amount) >= 0;

  return (
    <div className="border border-surface-border rounded-lg bg-white p-4 sm:p-5 space-y-3">
      <h3 className="text-sm font-bold text-text-body">年収累計の手入力 (確定額)</h3>
      <p className="text-xs text-text-sub">
        給与ソフトからの自動取込が無い場合、月次の確定支給額をここから記録できます。年収の壁の判定はこの確定額の累計を正として行われます。
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="sm:col-span-2">
          <label className="block text-xs text-text-sub mb-1">対象社員</label>
          <Select value={employeeId || undefined} onValueChange={setEmployeeId}>
            <SelectTrigger className="min-h-[44px]"><SelectValue placeholder="社員を選ぶ" /></SelectTrigger>
            <SelectContent>
              {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.family_name} {e.given_name} ({e.employee_number})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1">対象年</label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}年</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1">対象月</label>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map((m) => <SelectItem key={m} value={m}>{Number(m)}月</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-end gap-3">
        <div className="w-40">
          <label className="block text-xs text-text-sub mb-1">確定支給額 (円)</label>
          <input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)}
            className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
        </div>
        <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>記録する</Button>
      </div>
    </div>
  );
}

export function MyIncomePage() {
  const user = useAuthStore((s) => s.user);
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'income_cap', 'manage');

  return (
    <div className="space-y-4">
      <MySummaryCard />
      {canManage && <AccumulationEntryPanel />}
    </div>
  );
}
